import { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera, RefreshCw, Check, Zap, Loader2, Trophy, Upload, ArrowRight, Play, SkipForward, Eye, X, RotateCcw, Users, Settings2, Bell, Hand, Heart, Crown, Shuffle, Flag, Star, Flame, Skull, Volume2, VolumeX, Disc3, Music, Smartphone, Tv, Laugh } from "lucide-react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { api } from "@/lib/api";
import { Avatar, OPTIONS, ShapeIcon, useRoomState } from "@/lib/gameUi";
import { JukeboxBar, ConnectSpotifyButton, useSpotify } from "@/components/game/jukebox";
import { MemePickPhone, MemeVotePhone, MemeRevealPhone } from "@/components/game/memeMobile";

const nextLabel = (state) => {
  const r = state.round;
  if (r && r.index + 1 < r.total) return "Next Round";
  return state.has_bonus ? "Bonus Round" : "Final Results";
};

const TIME_OPTS = [10, 15, 20, 30];
const PERROUND_OPTS = [8, 10, 12, 15];
const ROUND_OPTS = [1, 2, 3, 4, 5, 6, 8];
const DIFFS = ["mixed", "easy", "medium", "hard"];

export const MODE_ICON = { fill_your_boots: Zap, buzzer_beater: Bell, point_snatcher: Hand, last_man_standing: Heart, hot_streaker: Flame, grassy_knoall: Skull, nominator: Crown, hit_that: Music, meme_caption: Laugh, random: Shuffle };

export function Center({ children }) { return <div className="flex-1 flex flex-col items-center justify-center text-center px-8 py-10">{children}</div>; }
export function Label({ children }) { return <p className="font-head uppercase tracking-[0.3em] text-cyan-300 text-sm mb-4 text-center">{children}</p>; }
export function BigButton({ children, onClick, disabled, testid, variant = "magenta" }) {
  const grad = variant === "green" ? "from-[#00FF66] to-[#00F0FF] text-[#0D0714] glow-green" : "from-[#FF2A85] to-[#8B5CF6] text-white glow-magenta";
  return <motion.button data-testid={testid} onClick={onClick} disabled={disabled} whileTap={{ scale: 0.97 }}
    className={`w-full mt-6 bg-gradient-to-r ${grad} rounded-2xl py-5 font-display font-black text-xl uppercase flex items-center justify-center gap-3 disabled:opacity-40`}>{children}</motion.button>;
}
function FieldLbl({ label, children }) { return <div><p className="font-head font-semibold text-xs uppercase tracking-widest text-cyan-300 mb-2">{label}</p>{children}</div>; }
function Pills({ options, value, onChange, labels, cap, testid }) {
  return <div className="flex flex-wrap gap-2">{options.map((o, i) => {
    const on = value === o; const lbl = labels ? labels[i] : cap ? String(o).charAt(0).toUpperCase() + String(o).slice(1) : o;
    return <button key={String(o)} data-testid={testid ? `${testid}-${o}` : undefined} onClick={() => onChange(o)}
      className={`px-4 py-2 rounded-full font-head font-bold text-sm transition-all border-2 ${on ? "bg-[#00F0FF] border-[#00F0FF] text-[#0D0714]" : "bg-white/5 border-white/15 text-white/70"}`}>{lbl}</button>;
  })}</div>;
}

// ---------- Host settings ----------
export function SettingsPanel({ onStart, busy, code, hostToken }) {
  const [categories, setCategories] = useState([]);
  const [modes, setModes] = useState([]);
  const spotify = useSpotify(code);
  const [time, setTime] = useState(20);
  const [diff, setDiff] = useState("mixed");
  const [cats, setCats] = useState([]);
  const [perRound, setPerRound] = useState(8);
  const [numRounds, setNumRounds] = useState(3);
  const [narration, setNarration] = useState(true);
  const [rating, setRating] = useState("family");
  const [roundModes, setRoundModes] = useState(["fill_your_boots", "random", "random"]);

  useEffect(() => {
    api.get("/categories").then((r) => setCategories(r.data.categories));
    api.get("/modes").then((r) => setModes(r.data.modes));
  }, []);

  const setRounds = (n) => {
    setNumRounds(n);
    setRoundModes((prev) => {
      const next = [...prev];
      while (next.length < n) next.push("random");
      next.length = n;
      if (next[0] === undefined) next[0] = "fill_your_boots";
      return next;
    });
  };
  const setMode = (i, m) => setRoundModes((prev) => prev.map((x, idx) => (idx === i ? m : x)));
  const toggleCat = (c) => setCats((s) => s.includes(c) ? s.filter((x) => x !== c) : [...s, c]);
  const randomizeAll = () => setRoundModes((prev) => prev.map(() => "random"));

  const start = () => onStart({
    time_per_question: time, difficulty: diff, categories: cats, narration, party_rating: rating,
    rounds: roundModes.map((m) => ({ mode: m, count: perRound })),
  });
  const hasParty = roundModes.includes("meme_caption");

  const modeName = (k) => k === "random" ? "🎲 Random" : (modes.find((m) => m.key === k)?.name || k);

  return (
    <div className="flex-1 px-5 py-6 overflow-y-auto">
      <div className="flex items-center gap-2 mb-6"><Settings2 className="w-6 h-6 text-[#00F0FF]" /><h1 className="font-display font-black text-2xl uppercase tracking-tight text-glow-cyan">Game Setup</h1></div>
      <div className="glass rounded-3xl p-5 space-y-6 border border-white/10">
        <FieldLbl label="Rounds"><Pills options={ROUND_OPTS} value={numRounds} onChange={setRounds} testid="setup-rounds" /></FieldLbl>
        <FieldLbl label="Questions per round"><Pills options={PERROUND_OPTS} value={perRound} onChange={setPerRound} testid="setup-perround" /><p className="text-white/40 text-xs font-head mt-2">Last Man Standing ignores this — it runs until one player remains.</p></FieldLbl>

        <div>
          <div className="flex items-center justify-between mb-2">
            <p className="font-head font-semibold text-xs uppercase tracking-widest text-cyan-300">Round modes</p>
            <button data-testid="setup-randomize" onClick={randomizeAll} className="flex items-center gap-1 text-xs font-head font-bold text-[#FFD700] bg-[#FFD700]/10 rounded-full px-3 py-1"><Shuffle className="w-3.5 h-3.5" /> Randomize</button>
          </div>
          <div className="space-y-2">
            {roundModes.map((m, i) => {
              const Icon = MODE_ICON[m] || Zap;
              return (
                <div key={i} className="flex items-center gap-3 bg-white/5 rounded-xl p-2.5 border border-white/10">
                  <span className="font-display font-black text-lg text-white/40 w-6 text-center">{i + 1}</span>
                  <Icon className="w-5 h-5 text-[#FF2A85] shrink-0" />
                  <select data-testid={`setup-roundmode-${i}`} value={m} onChange={(e) => setMode(i, e.target.value)}
                    className="flex-1 bg-[#170D26] border border-white/15 rounded-lg px-3 py-2 font-head font-semibold text-white text-sm outline-none focus:border-[#FF2A85]">
                    <option value="random">🎲 Random</option>
                    {modes.map((mo) => <option key={mo.key} value={mo.key}>{mo.name}</option>)}
                  </select>
                </div>
              );
            })}
          </div>
        </div>

        {hasParty && (
          <FieldLbl label="Caption cards — how cheeky?">
            <Pills options={["family", "adult"]} value={rating} onChange={setRating} labels={["Family-friendly", "Adults only (18+)"]} testid="setup-rating" />
            <p className="text-white/40 text-xs font-head mt-2">{rating === "adult" ? "Rude, edgy cards about dating, drinking and life chaos." : "Clean, kid- and work-safe captions."} Up to 8 memes per round.</p>
          </FieldLbl>
        )}

        <FieldLbl label="Seconds per question"><Pills options={TIME_OPTS} value={time} onChange={setTime} testid="setup-time" /></FieldLbl>
        <FieldLbl label="Difficulty"><Pills options={DIFFS} value={diff} onChange={setDiff} testid="setup-diff" cap /></FieldLbl>
        <FieldLbl label="TV narration — reads questions & intros aloud">
          <Pills options={[true, false]} value={narration} onChange={setNarration} labels={["On", "Off"]} testid="setup-narration" />
          <p className="text-white/40 text-xs font-head mt-2">A lively host voice plays on the TV. Turn off for a quieter game.</p>
        </FieldLbl>
        <FieldLbl label="Themed packs (none = all topics)">
          <p className="text-white/40 text-xs font-head mb-2">Pick any mix of themes — questions are drawn only from what you select.</p>
          <div className="flex flex-wrap gap-2">{categories.map((c) => {
            const on = cats.includes(c);
            return <button key={c} data-testid={`setup-cat-${c}`} onClick={() => toggleCat(c)}
              className={`px-3 py-1.5 rounded-full font-head font-semibold text-xs border-2 transition-all ${on ? "bg-[#FF2A85] border-[#FF2A85] text-white" : "bg-white/5 border-white/15 text-white/70"}`}>{c}</button>;
          })}</div>
        </FieldLbl>
      </div>
      {spotify?.configured && (
        <div className="mt-5">
          {spotify.connected ? (
            <div className="glass rounded-2xl p-4 border border-[#1DB954]/40 flex items-center gap-3">
              <Disc3 className="w-6 h-6 text-[#1DB954] shrink-0" />
              <div className="flex-1 min-w-0"><p className="font-head font-bold text-white text-sm">Spotify connected</p><p className="font-head text-white/50 text-xs truncate">{spotify.display_name}{spotify.is_premium ? "" : " · Premium required to play"}</p></div>
            </div>
          ) : (
            <ConnectSpotifyButton code={code} hostToken={hostToken} />
          )}
          <p className="text-white/40 text-xs font-head mt-2 text-center">Optional: add a Spotify soundtrack — the host controls the music</p>
        </div>
      )}
      <BigButton testid="control-start-lobby" disabled={busy} onClick={start} variant="green">{busy ? <Loader2 className="w-6 h-6 animate-spin" /> : <>Start Lobby <ArrowRight className="w-6 h-6" /></>}</BigButton>
    </div>
  );
}

// ---------- Join self ----------
export function JoinSelf({ code, onJoined, isHost }) {
  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [fav, setFav] = useState(null);
  const [cats, setCats] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [joining, setJoining] = useState(false);
  const videoRef = useRef(null);
  const [stream, setStream] = useState(null);
  const [camOn, setCamOn] = useState(false);
  const [camError, setCamError] = useState(false);

  useEffect(() => { api.get("/categories").then((r) => setCats(r.data.categories)).catch(() => {}); }, []);
  const [existing, setExisting] = useState([]);
  useEffect(() => { api.get(`/rooms/${code}/state`).then((r) => setExisting(r.data.players || [])).catch(() => {}); }, [code]);
  const doRejoin = async (p) => {
    setJoining(true);
    try {
      const res = await api.post(`/rooms/${code}/rejoin`, { player_id: p.id });
      onJoined({ player_id: res.data.player_id, player_token: res.data.player_token, name: res.data.name, color: res.data.color, avatar_url: res.data.avatar_url });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not rejoin"); }
    setJoining(false);
  };

  // attach stream once the <video> is actually mounted (fixes black preview)
  useEffect(() => {
    if (camOn && stream && videoRef.current) {
      videoRef.current.srcObject = stream;
      videoRef.current.play().catch(() => {});
    }
  }, [camOn, stream]);

  const stopCam = useCallback(() => {
    setStream((s) => { if (s) s.getTracks().forEach((t) => t.stop()); return null; });
    setCamOn(false);
  }, []);
  useEffect(() => () => { if (stream) stream.getTracks().forEach((t) => t.stop()); }, [stream]);

  const startCam = async () => {
    setCamError(false);
    try {
      const s = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      setStream(s); setCamOn(true);
    } catch (e) { setCamError(true); toast.error("Camera blocked — use Upload instead"); }
  };
  const uploadBlob = async (blob) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", blob, "avatar.jpg");
      const res = await api.post("/upload-avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAvatarUrl(res.data.url);
    } catch (e) { toast.error("Upload failed"); }
    setUploading(false);
  };
  const capture = () => {
    const v = videoRef.current; if (!v || !v.videoWidth) return;
    const c = document.createElement("canvas"); const s = Math.min(v.videoWidth, v.videoHeight);
    c.width = s; c.height = s;
    c.getContext("2d").drawImage(v, (v.videoWidth - s) / 2, (v.videoHeight - s) / 2, s, s, 0, 0, s, s);
    stopCam();
    c.toBlob((b) => b && uploadBlob(b), "image/jpeg", 0.9);
  };
  const onFile = (e) => { const f = e.target.files?.[0]; if (f) uploadBlob(f); };
  const doJoin = async () => {
    setJoining(true);
    try {
      const res = await api.post(`/rooms/${code}/join`, { name: name.trim(), avatar_url: avatarUrl, favourite_category: fav });
      onJoined({ player_id: res.data.player_id, player_token: res.data.player_token, name: res.data.name, color: res.data.color, avatar_url: avatarUrl });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not join"); }
    setJoining(false);
  };

  return (
    <div className="flex-1 flex flex-col px-6 py-8 max-w-md w-full mx-auto">
      {isHost && <p className="text-center font-head text-[#FFD700] text-sm mb-3">You're the host — join in to play too!</p>}
      <AnimatePresence mode="wait">
        {step === 0 && (
          <motion.div key="name" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} className="glass rounded-3xl p-6 border border-white/10">
            {existing.length > 0 && (
              <div data-testid="rejoin-panel" className="mb-5 pb-5 border-b border-white/10">
                <p className="font-head text-sm text-white/55 text-center mb-3">Already played? <span className="text-[#00FF66] font-bold">Tap your name to rejoin</span> (your score is kept)</p>
                <div className="flex flex-wrap justify-center gap-2">
                  {existing.map((p) => (
                    <button key={p.id} data-testid={`rejoin-${p.id}`} disabled={joining} onClick={() => doRejoin(p)}
                      className="flex items-center gap-2 bg-white/5 rounded-full pl-1.5 pr-4 py-1.5 border-2 border-white/15 hover:border-[#00FF66]">
                      <Avatar player={p} size={30} ring={false} /><span className="font-head font-bold text-white text-sm max-w-[8rem] truncate">{p.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <Label>Your Name</Label>
            <input data-testid="mobile-nickname-input" value={name} onChange={(e) => setName(e.target.value.slice(0, 12))} placeholder="Nickname" autoFocus maxLength={12}
              className="w-full text-center font-display font-bold text-3xl bg-white/5 border-2 border-white/20 rounded-2xl py-5 text-white focus:border-[#FF2A85] outline-none" />
            <p className="text-white/40 text-sm text-center mt-2 font-head">{name.length}/12</p>
            <BigButton testid="mobile-name-continue" disabled={!name.trim()} onClick={() => setStep(1)}>Add Photo <ArrowRight className="w-6 h-6" /></BigButton>
          </motion.div>
        )}
        {step === 1 && (
          <motion.div key="photo" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} className="glass rounded-3xl p-6 border border-white/10">
            <Label>Your Avatar</Label>
            <div className="relative rounded-3xl overflow-hidden bg-black/40 border-2 border-white/15 aspect-square flex items-center justify-center mb-4" data-testid="mobile-camera-preview">
              {avatarUrl ? <Avatar player={{ avatar_url: avatarUrl, color: "#FF2A85" }} size={240} ring={false} />
                : <video ref={videoRef} playsInline muted className={`w-full h-full object-cover ${camOn ? "" : "hidden"}`} style={{ transform: "scaleX(-1)" }} />}
              {!avatarUrl && !camOn && <div className="absolute text-center px-6"><Camera className="w-14 h-14 text-white/30 mx-auto mb-3" /><p className="text-white/50 font-head">Take a selfie or upload</p>{camError && <p className="text-rose-400 text-sm mt-2">Camera blocked — use Upload</p>}</div>}
              {uploading && <div className="absolute inset-0 bg-black/60 flex items-center justify-center"><Loader2 className="w-10 h-10 text-[#FF2A85] animate-spin" /></div>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {!avatarUrl && !camOn && <button data-testid="mobile-take-photo-button" onClick={startCam} className="bg-[#00F0FF]/15 border-2 border-[#00F0FF]/50 rounded-2xl py-4 font-head font-bold text-[#00F0FF] flex items-center justify-center gap-2"><Camera className="w-5 h-5" /> Camera</button>}
              {camOn && <button data-testid="mobile-capture-button" onClick={capture} className="bg-[#FF2A85] rounded-2xl py-4 font-head font-bold text-white flex items-center justify-center gap-2 glow-magenta col-span-2"><Check className="w-5 h-5" /> Capture</button>}
              {!camOn && <label className="bg-white/5 border-2 border-white/15 rounded-2xl py-4 font-head font-bold text-white/80 flex items-center justify-center gap-2 cursor-pointer"><Upload className="w-5 h-5" /> Gallery<input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="mobile-upload-input" /></label>}
              {avatarUrl && <button onClick={() => setAvatarUrl(null)} className="col-span-2 bg-white/5 border-2 border-white/15 rounded-2xl py-3 font-head font-semibold text-white/70 flex items-center justify-center gap-2"><RefreshCw className="w-4 h-4" /> Retake</button>}
            </div>
            <BigButton testid="mobile-join-lobby-button" disabled={joining} onClick={() => setStep(2)}>Pick a Favourite <ArrowRight className="w-6 h-6" /></BigButton>
            <button onClick={() => setStep(2)} disabled={joining} className="mt-3 w-full text-white/40 text-sm font-head underline">Skip photo</button>
          </motion.div>
        )}
        {step === 2 && (
          <motion.div key="fav" initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -30 }} className="glass rounded-3xl p-6 border border-white/10">
            <Label>Your Favourite Topic</Label>
            <p className="text-white/55 text-sm text-center mb-4 font-head">At the end you'll get a bonus question from this — <span className="text-[#FFD700] font-bold">Favou-Rite or Favou-Rong!</span> <span className="text-[#00FF66]">+1000</span> / <span className="text-rose-400">−1000</span></p>
            <div className="grid grid-cols-2 gap-2 max-h-[44vh] overflow-y-auto pr-1">
              <button data-testid="fav-cat-surprise" onClick={() => setFav(null)}
                className={`px-3 py-2.5 rounded-xl font-head font-semibold text-sm border-2 transition-all flex items-center justify-center gap-1.5 ${fav === null ? "bg-[#FFD700] border-[#FFD700] text-[#0D0714]" : "bg-white/5 border-white/15 text-white/70"}`}><Shuffle className="w-4 h-4" /> Surprise me</button>
              {cats.map((c) => {
                const on = fav === c;
                return <button key={c} data-testid={`fav-cat-${c}`} onClick={() => setFav(c)}
                  className={`px-3 py-2.5 rounded-xl font-head font-semibold text-sm border-2 transition-all ${on ? "bg-[#FF2A85] border-[#FF2A85] text-white" : "bg-white/5 border-white/15 text-white/70"}`}>{c}</button>;
              })}
            </div>
            <BigButton testid="mobile-confirm-join-button" disabled={joining} onClick={doJoin}>{joining ? <Loader2 className="w-6 h-6 animate-spin" /> : <>{isHost ? "Join & Continue" : "Join Game"} <Zap className="w-6 h-6" fill="currentColor" /></>}</BigButton>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------- Host console (recovery: drive the game without a player seat) ----------
export function HostConsole({ code, hostToken }) {
  const { state } = useRoomState(code, null);
  const [busy, setBusy] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const act = async (path) => {
    setBusy(true);
    try { await api.post(`/rooms/${code}/${path}`, { host_token: hostToken }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    setBusy(false);
  };
  const narrationOn = state?.settings?.narration !== false;
  const toggleNarration = async () => {
    try { await api.post(`/rooms/${code}/settings`, { host_token: hostToken, settings: { narration: !narrationOn } }); }
    catch (e) { toast.error("Couldn't toggle narration"); }
  };
  if (!state) return <div className="neon-bg min-h-screen flex items-center justify-center"><Loader2 className="w-10 h-10 text-[#FF2A85] animate-spin" /></div>;
  const r = state.round;
  const s = state.status;
  const lastStep = r ? (r.q_index + 1 >= r.q_total || state.payouts != null) : false;
  return (
    <div className="neon-bg min-h-screen flex flex-col relative">
      <div className="relative z-10 flex flex-col min-h-screen p-5">
        <div className="flex items-center gap-2 mb-1"><Crown className="w-6 h-6 text-[#FFD700]" /><h1 className="font-display font-black text-2xl uppercase text-white">Host Console</h1></div>
        <p className="font-head text-white/50 text-sm mb-6">Game {code} · you're driving the game (not playing this round)</p>
        <div className="glass rounded-3xl p-6 border border-white/10 flex-1 flex flex-col items-center justify-center text-center gap-3">
          <p className="font-head uppercase tracking-[0.3em] text-cyan-300 text-xs">Status</p>
          <p className="font-display font-black text-3xl text-white capitalize">{s.replace(/_/g, " ")}</p>
          {r && <p className="font-head text-white/60">Round {r.index + 1}/{r.total} · {r.mode_name} · Q{r.q_index + 1}/{r.q_total}</p>}
          <div className="flex items-center gap-2 mt-2"><Users className="w-4 h-4 text-[#FFD700]" /><span className="font-bold text-white">{state.player_count} players</span></div>
          <button data-testid="console-narration-toggle" onClick={toggleNarration}
            className={`mt-3 rounded-full px-4 py-2 flex items-center gap-2 font-head font-bold text-sm border transition-colors ${narrationOn ? "border-[#00FF66]/50 text-[#00FF66]" : "border-white/15 text-white/40"}`}>
            {narrationOn ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />} TV narration {narrationOn ? "On" : "Off"}
          </button>
        </div>
        <div className="mt-5">
          {s === "lobby" && <BigButton testid="console-start" disabled={busy || !state.player_count} onClick={() => act("start")} variant="green"><Play className="w-6 h-6" fill="currentColor" /> Start Game</BigButton>}
          {s === "round_intro" && <BigButton testid="console-begin" disabled={busy} onClick={() => act("begin-round")} variant="green"><Play className="w-6 h-6" fill="currentColor" /> Begin Round {r ? r.index + 1 : ""}</BigButton>}
          {s === "question" && <BigButton testid="console-reveal" disabled={busy} onClick={() => act("reveal")}><Eye className="w-6 h-6" /> Reveal now</BigButton>}
          {(s === "meme_pick" || s === "meme_vote") && <BigButton testid="console-meme-skip" disabled={busy} onClick={() => act("reveal")}><SkipForward className="w-6 h-6" /> {s === "meme_pick" ? "Start voting now" : "Close voting"}</BigButton>}
          {(s === "reveal" || s === "meme_reveal") && <BigButton testid="console-next" disabled={busy} onClick={() => act("next")}>{lastStep ? <><Trophy className="w-6 h-6" /> {nextLabel(state)}</> : <><SkipForward className="w-6 h-6" /> Next</>}</BigButton>}
          {s === "steal" && <p className="text-center text-white/50 font-head py-4">Waiting for the snatcher to pick…</p>}
          {s === "assassinate" && <p className="text-center text-white/50 font-head py-4">Fastest finger is choosing who to assassinate…</p>}
          {s === "nominate" && <p className="text-center text-white/50 font-head py-4">Waiting for the leader to play or pass…</p>}
          {s === "bonus_intro" && <BigButton testid="console-begin-bonus" disabled={busy} onClick={() => act("begin-round")} variant="green"><Star className="w-6 h-6" fill="currentColor" /> Start Bonus Round</BigButton>}
          {s === "bonus" && <BigButton testid="console-reveal-bonus" disabled={busy} onClick={() => act("reveal")}><Eye className="w-6 h-6" /> Reveal Bonus</BigButton>}
          {s === "bonus_reveal" && <BigButton testid="console-bonus-final" disabled={busy} onClick={() => act("next")}><Trophy className="w-6 h-6" /> Final Results</BigButton>}
          {s === "finished" && <BigButton testid="console-restart" disabled={busy} onClick={() => act("restart")} variant="green"><RotateCcw className="w-5 h-5" /> Play Again</BigButton>}
          {s === "finished" && <button data-testid="console-change-settings" disabled={busy} onClick={() => act("reconfigure")} className="w-full mt-3 glass rounded-2xl py-4 font-display font-black text-lg uppercase text-white border border-white/15 flex items-center justify-center gap-2"><Settings2 className="w-5 h-5" /> Change Settings</button>}
          {s === "finished" && <button data-testid="console-new-players" disabled={busy} onClick={() => act("full-reset")} className="w-full mt-3 rounded-2xl py-3.5 font-head font-bold text-sm uppercase border border-rose-500/40 text-rose-300 flex items-center justify-center gap-2"><Users className="w-4 h-4" /> New Players (full reset)</button>}
          {s !== "finished" && s !== "lobby" && (
            <button data-testid="console-end-game" disabled={busy}
              onClick={() => { if (confirmReset) { setConfirmReset(false); act("full-reset"); } else { setConfirmReset(true); setTimeout(() => setConfirmReset(false), 4000); } }}
              className={`w-full mt-3 rounded-2xl py-3.5 font-head font-bold text-sm uppercase border flex items-center justify-center gap-2 ${confirmReset ? "bg-rose-600 border-rose-500 text-white" : "glass border-rose-500/40 text-rose-300"}`}>
              <RotateCcw className="w-4 h-4" /> {confirmReset ? "Tap again — clear everyone & reset" : "End Game / Reset Room"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------- Unified controller ----------
function CastToTV({ code, remote }) {
  const tvUrl = `${window.location.origin}/host?code=${code}&tv=1`;
  const [available, setAvailable] = useState(false);
  const [open, setOpen] = useState(false);
  const reqRef = useRef(null);
  const supported = typeof window !== "undefined" && "PresentationRequest" in window;

  useEffect(() => {
    if (!supported) return;
    let avail = null;
    let onChange = null;
    try {
      const req = new window.PresentationRequest([tvUrl]);
      reqRef.current = req;
      req.getAvailability().then((a) => {
        avail = a; setAvailable(!!a.value);
        onChange = () => setAvailable(!!a.value);
        a.addEventListener("change", onChange);
      }).catch(() => {});
    } catch (e) { /* availability monitoring unsupported */ }
    return () => { if (avail && onChange) { try { avail.removeEventListener("change", onChange); } catch (e) { /* ignore */ } } };
  }, [tvUrl, supported]);

  const cast = async () => {
    if (!reqRef.current) { setOpen(true); return; }
    try { await reqRef.current.start(); toast.success("Showing the game on your TV 📺"); }
    catch (e) { setOpen(true); }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(tvUrl); toast.success("TV link copied!"); }
    catch (e) { toast.error(tvUrl); }
  };

  if (!remote) return null;
  const nearby = supported && available;
  return (
    <>
      <button data-testid="remote-cast-tv" onClick={nearby ? cast : () => setOpen(true)}
        className={`mx-4 mt-3 rounded-2xl py-2.5 px-4 font-head font-bold text-sm border flex items-center justify-center gap-2 ${nearby ? "border-[#00F0FF]/60 text-[#00F0FF] bg-[#00F0FF]/10 glow-cyan" : "border-[#00F0FF]/30 text-[#00F0FF]/90 bg-[#00F0FF]/5"}`}>
        <Tv className="w-4 h-4" /> {nearby ? "TV nearby — show the game on it 📺" : "Show the game on a TV 📺"}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)}
            className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4">
            <motion.div initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }} onClick={(e) => e.stopPropagation()}
              className="glass rounded-3xl p-6 border border-[#00F0FF]/30 w-full max-w-sm text-center">
              <div className="flex items-center justify-between mb-4"><p className="font-display font-black text-xl text-white flex items-center gap-2"><Tv className="w-5 h-5 text-[#00F0FF]" /> Show on a TV</p><button data-testid="cast-sheet-close" onClick={() => setOpen(false)} className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center"><X className="w-4 h-4 text-white" /></button></div>
              <p className="font-head text-white/60 text-sm mb-4">Open your TV's web browser and go to this link — it'll mirror the game, synced to the host. Perfect for a second living room.</p>
              <div data-testid="cast-tv-qr" className="bg-white p-4 rounded-2xl inline-block mb-4"><QRCodeSVG value={tvUrl} size={200} level="M" /></div>
              <p className="font-head text-white/40 text-xs break-all mb-3">{tvUrl}</p>
              <button data-testid="cast-copy-link" onClick={copyLink} className="w-full rounded-2xl py-3 font-head font-bold text-sm bg-[#00F0FF] text-[#0D0714] flex items-center justify-center gap-2"><Smartphone className="w-4 h-4" /> Copy TV link</button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export function GameController({ code, session, isHost, hostToken, remote, onLeave, roomState }) {
  const own = useRoomState(roomState ? null : code, session.player_id);
  const { state, error } = roomState || own;
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [endConfirm, setEndConfirm] = useState(false);
  const me = state?.players?.find((p) => p.id === session.player_id);
  const status = state?.status;
  const r = state?.round;
  useEffect(() => { setPicked(null); }, [r?.index, r?.q_index, status]);
  const answered = state?.my_answer != null || picked != null;

  const hostAct = async (path, extra = {}) => {
    setBusy(true);
    try { await api.post(`/rooms/${code}/${path}`, { host_token: hostToken, ...extra }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    setBusy(false);
  };
  const submit = async (choice) => {
    if (answered || busy) return;
    setPicked(choice);
    if (navigator.vibrate) navigator.vibrate(40);
    try { await api.post(`/rooms/${code}/answer`, { player_id: session.player_id, player_token: session.player_token, choice }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not submit"); setPicked(null); }
  };
  const doNominate = async (action, target_id) => {
    setBusy(true);
    try { await api.post(`/rooms/${code}/nominate`, { player_id: session.player_id, player_token: session.player_token, action, target_id }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const doSteal = async (target_id) => {
    setBusy(true);
    try { await api.post(`/rooms/${code}/steal`, { player_id: session.player_id, player_token: session.player_token, target_id }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  const narrationOn = state?.settings?.narration !== false;
  const toggleNarration = async () => {
    try { await api.post(`/rooms/${code}/settings`, { host_token: hostToken, settings: { narration: !narrationOn } }); }
    catch (e) { toast.error("Couldn't toggle narration"); }
  };
  const copyRemoteInvite = async () => {
    const link = `${window.location.origin}/play?code=${code}&remote=1`;
    try { await navigator.clipboard.writeText(link); toast.success("Remote invite link copied!"); }
    catch (e) { toast.error(link); }
  };

  if (!state) return <div className="neon-bg min-h-screen flex flex-col items-center justify-center gap-4"><Loader2 className="w-10 h-10 text-[#FF2A85] animate-spin" /><p className="text-white/60 font-head">{error ? "Reconnecting…" : "Loading…"}</p></div>;
  if (!me) return <div className="neon-bg min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center"><p className="font-display font-bold text-2xl text-white">You left the game</p><button onClick={onLeave} className="bg-[#FF2A85] rounded-2xl px-6 py-3 font-head font-bold text-white">Back</button></div>;

  const isNominator = state.nominator_id === me.id;
  const amActive = state.active_player === me.id;
  const amStealer = state.steal_by === me.id;
  const amAssassin = state.assassin_id === me.id;
  const lives = me.lives;
  const maxLives = r?.mode === "grassy_knoall" ? 2 : 3;
  const others = state.players.filter((p) => p.id !== me.id);
  const doAssassinate = async (target_id) => {
    setBusy(true);
    try { await api.post(`/rooms/${code}/assassinate`, { player_id: session.player_id, player_token: session.player_token, target_id }); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  return (
    <div className="neon-bg min-h-screen flex flex-col relative">
      <div className="relative z-10 flex flex-col min-h-screen">
        <div className="flex items-center gap-3 px-5 py-3 glass border-b border-white/10">
          <Avatar player={me} size={44} />
          <div className="flex-1 min-w-0">
            <p className="font-head font-bold text-white truncate">{me.name}{isHost && <span className="ml-2 text-xs text-[#FFD700]">HOST</span>}</p>
            <p className="text-xs text-white/50 font-head">{r ? `R${r.index + 1} · ${r.mode_name}` : `Game ${code}`}</p>
          </div>
          {lives != null ? <div className="flex gap-0.5">{Array.from({ length: maxLives }).map((_, i) => <Heart key={i} className="w-5 h-5" fill={i < lives ? "#FF2A85" : "transparent"} color={i < lives ? "#FF2A85" : "#555"} />)}</div>
            : <div className="text-right"><p className="font-display font-black text-2xl text-[#00F0FF] leading-none">{me.score}</p><p className="text-xs text-white/50 font-head">points</p></div>}
          {isHost && (
            <button data-testid="control-narration-toggle" onClick={toggleNarration} title={narrationOn ? "Narration on — tap to mute TV voice" : "Narration off — tap to enable TV voice"}
              className={`ml-1 shrink-0 rounded-full w-10 h-10 flex items-center justify-center border transition-colors ${narrationOn ? "border-[#00FF66]/50 text-[#00FF66]" : "border-white/15 text-white/40"}`}>
              {narrationOn ? <Volume2 className="w-5 h-5" /> : <VolumeX className="w-5 h-5" />}
            </button>
          )}
          {isHost && status !== "finished" && status !== "lobby" && (
            <button data-testid="control-end-game" disabled={busy}
              onClick={async () => { if (endConfirm) { setEndConfirm(false); await hostAct("full-reset"); onLeave && onLeave(); } else { setEndConfirm(true); setTimeout(() => setEndConfirm(false), 4000); } }}
              className={`ml-2 shrink-0 rounded-full px-3 py-2 font-head font-bold text-xs uppercase border flex items-center gap-1.5 ${endConfirm ? "bg-rose-600 border-rose-500 text-white" : "border-rose-500/40 text-rose-300"}`}>
              <RotateCcw className="w-4 h-4" /> {endConfirm ? "Confirm reset" : "End"}
            </button>
          )}
        </div>
        {isHost && endConfirm && status !== "finished" && status !== "lobby" && (
          <div className="px-5 py-2 bg-rose-600/15 border-b border-rose-500/30 text-center font-head text-sm text-rose-200">Fully resets the room — removes all players so everyone re-joins fresh. Tap <span className="font-bold">Confirm reset</span> again.</div>
        )}

        <CastToTV code={code} remote={remote} />

        {status === "setup" && (
          <Center><Loader2 className="w-12 h-12 text-[#FF2A85] animate-spin mb-4" /><p className="font-display font-black text-2xl text-white">Setting up the next game…</p><p className="text-white/60 font-head mt-2">The host is choosing new settings 📺</p></Center>
        )}

        {status === "lobby" && (isHost ? (
          <div className="flex-1 flex flex-col p-5">
            <div className="flex items-center justify-between mb-4"><p className="font-display font-black text-xl text-white">Lobby</p><span className="glass rounded-full px-3 py-1 flex items-center gap-1 border border-white/10"><Users className="w-4 h-4 text-[#FFD700]" /><span className="font-bold">{state.player_count}/12</span></span></div>
            <p className="text-white/50 font-head text-sm mb-4">Players scan the TV's QR to join. Start when everyone's in.</p>
            <button data-testid="control-copy-remote-invite" onClick={copyRemoteInvite} className="mb-4 w-full rounded-2xl py-3 font-head font-bold text-sm border border-[#00F0FF]/40 text-[#00F0FF] flex items-center justify-center gap-2"><Smartphone className="w-4 h-4" /> Copy remote invite link 🌐</button>
            <div className="flex-1 grid grid-cols-3 gap-3 content-start overflow-y-auto">
              {state.players.map((p) => (
                <div key={p.id} className="relative flex flex-col items-center gap-1 bg-white/5 rounded-2xl p-3 border border-white/10">
                  <Avatar player={p} size={52} /><span className="font-head text-xs text-white truncate max-w-full">{p.name}</span>
                  {p.id !== me.id && <button data-testid={`control-kick-${p.id}`} onClick={() => hostAct("kick", { player_id: p.id })} className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-rose-600 flex items-center justify-center"><X className="w-3.5 h-3.5 text-white" /></button>}
                </div>
              ))}
            </div>
            <BigButton testid="control-start-game" disabled={busy || state.player_count === 0} onClick={() => hostAct("start")} variant="green"><Play className="w-6 h-6" fill="currentColor" /> Start Game</BigButton>
          </div>
        ) : (
          <Center><motion.div animate={{ scale: [1, 1.08, 1] }} transition={{ repeat: Infinity, duration: 1.6 }}><Check className="w-16 h-16 text-[#00FF66] mx-auto mb-4" /></motion.div><p className="font-display font-black text-3xl text-white">You're in!</p><p className="text-white/60 font-head mt-2">{remote ? "Questions will appear on your screen 📱" : "Look at the TV screen 📺"}</p></Center>
        ))}

        {status === "round_intro" && r && (
          <div className="flex-1 flex flex-col">
            <RoundBanner r={r} />
            {isHost ? <div className="p-4"><BigButton testid="control-begin-round" disabled={busy} onClick={() => hostAct("begin-round")} variant="green"><Play className="w-6 h-6" fill="currentColor" /> Begin Round {r.index + 1}</BigButton></div>
              : <p className="text-center text-white/50 font-head pb-8">{remote ? "Get ready… the question's coming to your screen 📱" : "Get ready… watch the TV 📺"}</p>}
          </div>
        )}

        {status === "nominate" && (
          <div className="flex-1 flex flex-col p-4">
            {isNominator ? (
              <>
                <p className="text-center font-display font-black text-2xl text-[#FFD700] mt-4">You're the Leader 👑</p>
                <p className="text-center text-white/60 font-head mb-5">Play it yourself, or pass it to a rival.</p>
                <BigButton testid="nominate-play" disabled={busy} onClick={() => doNominate("play")} variant="green"><Play className="w-6 h-6" fill="currentColor" /> Play it myself</BigButton>
                <p className="text-center text-white/40 font-head text-sm mt-6 mb-2">— or pass to —</p>
                <div className="grid grid-cols-2 gap-3 overflow-y-auto">
                  {others.map((p) => (
                    <button key={p.id} data-testid={`nominate-pass-${p.id}`} disabled={busy} onClick={() => doNominate("pass", p.id)}
                      className="flex items-center gap-2 bg-white/5 rounded-2xl p-3 border-2 border-white/10 hover:border-[#FF2A85]"><Avatar player={p} size={40} /><span className="font-head font-bold text-white text-sm truncate">{p.name}</span></button>
                  ))}
                </div>
              </>
            ) : <Center><Crown className="w-14 h-14 text-[#FFD700] mx-auto mb-4" /><p className="font-display font-black text-2xl text-white">The Nominator</p><p className="text-white/60 font-head mt-2">The leader is deciding to play or pass…</p></Center>}
          </div>
        )}

        {status === "question" && state.current && (
          <div className="flex-1 flex flex-col p-4 gap-3">
            {(state.current.loading && !answered) ? <GetReadyNote /> :
              state.can_answer ? (
              <>
                <p data-testid="mobile-answer-status" className="text-center font-head text-white/60 text-sm">{r?.mode === "nominator" ? (state.nominate_action === "pass" ? "Passed to you — go!" : "Your question ⚡") : (state.current.mode === "hit_that" && state.current.audio_uri) ? "🎧 Listen on the TV — then answer" : "Tap your answer ⚡"}</p>
                {state.current.mode === "hit_that" && state.current.audio_uri ? <div className="glass rounded-2xl px-4 py-3 border border-[#1DB954]/40 text-center"><p className="font-display font-bold text-lg text-white flex items-center justify-center gap-2"><Music className="w-5 h-5 text-[#1DB954]" /> {state.current.question}</p>{remote && <p className="text-white/40 font-head text-xs mt-1">🎧 Audio plays on the host's TV</p>}</div>
                  : remote && <RemoteQuestion state={state} />}
                <div className="flex-1 grid grid-cols-1 gap-3">
                  {state.current.options.map((opt, i) => { const o = OPTIONS[i]; return (
                    <motion.button key={i} data-testid={`mobile-choice-${o.key}`} onClick={() => submit(i)} whileTap={{ scale: 0.95 }}
                      className={`${o.bg} ${o.active} ${o.glow} rounded-3xl flex items-center gap-4 px-6 border-4 ${o.border} flex-1`}>
                      <ShapeIcon index={i} className="w-9 h-9 text-white/90" /><span className="font-head font-bold text-xl text-white text-left">{opt}</span>
                    </motion.button>); })}
                </div>
              </>
            ) : answered ? <LockedIn choice={state.my_answer ?? picked} remote={remote} />
              : <SpectatorNote state={state} me={me} remote={remote} />}
            {isHost && <button data-testid="control-reveal" onClick={() => hostAct("reveal")} className="glass rounded-2xl py-3 font-head font-bold text-white/80 border border-white/15 flex items-center justify-center gap-2"><Eye className="w-5 h-5" /> Reveal now</button>}
          </div>
        )}

        {status === "steal" && (
          <div className="flex-1 flex flex-col p-4">
            {amStealer ? (
              <>
                <p className="text-center font-display font-black text-2xl text-[#00FF66] mt-4">Nailed it! 💰</p>
                <p className="text-center text-white/60 font-head mb-5">Steal {400} points — pick your victim:</p>
                <div className="grid grid-cols-1 gap-3 overflow-y-auto">
                  {others.map((p) => (
                    <button key={p.id} data-testid={`steal-${p.id}`} disabled={busy} onClick={() => doSteal(p.id)}
                      className="flex items-center gap-3 bg-white/5 rounded-2xl p-3 border-2 border-white/10 hover:border-[#FF2A85]"><Avatar player={p} size={44} /><span className="font-head font-bold text-white flex-1 text-left truncate">{p.name}</span><span className="font-display font-black text-[#00F0FF]">{p.score}</span></button>
                  ))}
                </div>
              </>
            ) : <Center><Hand className="w-14 h-14 text-[#FF2A85] mx-auto mb-4" /><p className="font-display font-black text-2xl text-white">Point Snatcher!</p><p className="text-white/60 font-head mt-2">{state.steal_by_name} is choosing who to rob…</p></Center>}
          </div>
        )}

        {status === "assassinate" && (
          <div className="flex-1 flex flex-col p-4">
            {amAssassin ? (
              <>
                <p className="text-center font-display font-black text-2xl text-[#00FF66] mt-4">Fastest finger! 💀</p>
                <p className="text-center text-white/60 font-head mb-5">You earned <span className="text-[#FFD700] font-bold">100</span> — now take a life from a rival:</p>
                <div className="grid grid-cols-1 gap-3 overflow-y-auto">
                  {others.filter((p) => (p.lives || 0) > 0).map((p) => (
                    <button key={p.id} data-testid={`assassinate-${p.id}`} disabled={busy} onClick={() => doAssassinate(p.id)}
                      className="flex items-center gap-3 bg-white/5 rounded-2xl p-3 border-2 border-white/10 hover:border-rose-500">
                      <Avatar player={p} size={44} /><span className="font-head font-bold text-white flex-1 text-left truncate">{p.name}</span>
                      <div className="flex gap-0.5">{Array.from({ length: 2 }).map((_, k) => <Heart key={k} className="w-4 h-4" fill={k < (p.lives || 0) ? "#FF2A85" : "transparent"} color={k < (p.lives || 0) ? "#FF2A85" : "#555"} />)}</div>
                    </button>
                  ))}
                </div>
              </>
            ) : <Center><Skull className="w-14 h-14 text-[#FF2A85] mx-auto mb-4" /><p className="font-display font-black text-2xl text-white">The Grassy Kno-all</p><p className="text-white/60 font-head mt-2">{state.assassin_name} answered fastest and is choosing who to assassinate…</p></Center>}
          </div>
        )}

        {status === "meme_pick" && state.current && (
          <div className="flex-1 flex flex-col">
            <MemePickPhone state={state} code={code} session={session} remote={remote} />
            {isHost && <div className="px-4 pb-4"><button data-testid="control-meme-skip-pick" onClick={() => hostAct("reveal")} className="w-full glass rounded-2xl py-3 font-head font-bold text-white/80 border border-white/15 flex items-center justify-center gap-2"><SkipForward className="w-5 h-5" /> Start voting now</button></div>}
          </div>
        )}
        {status === "meme_vote" && state.current && (
          <div className="flex-1 flex flex-col">
            <MemeVotePhone state={state} code={code} session={session} remote={remote} />
            {isHost && <div className="px-4 pb-4"><button data-testid="control-meme-close-vote" onClick={() => hostAct("reveal")} className="w-full glass rounded-2xl py-3 font-head font-bold text-white/80 border border-white/15 flex items-center justify-center gap-2"><SkipForward className="w-5 h-5" /> Close voting</button></div>}
          </div>
        )}
        {status === "meme_reveal" && state.current && (
          <div className="flex-1 flex flex-col">
            <MemeRevealPhone state={state} me={me} />
            {isHost && <div className="p-4"><BigButton testid="control-next" disabled={busy} onClick={() => hostAct("next")}>{lastStep(state) ? <><Trophy className="w-6 h-6" /> {nextLabel(state)}</> : <><SkipForward className="w-6 h-6" /> Next meme</>}</BigButton></div>}
          </div>
        )}
        {status === "reveal" && (
          <div className="flex-1 flex flex-col">
            <RevealResult state={state} me={me} remote={remote} />
            {remote && state.current && <RemoteReveal state={state} me={me} />}
            {isHost && <div className="p-4"><BigButton testid="control-next" disabled={busy} onClick={() => hostAct("next")}>{lastStep(state) ? <><Trophy className="w-6 h-6" /> {nextLabel(state)}</> : <><SkipForward className="w-6 h-6" /> Next</>}</BigButton></div>}
          </div>
        )}

        {status === "bonus_intro" && (
          <div className="flex-1 flex flex-col">
            <Center>
              <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring" }} className="w-24 h-24 rounded-3xl bg-[#FFD700]/20 glow-magenta flex items-center justify-center mb-5"><Star className="w-12 h-12 text-[#FFD700]" fill="#FFD700" /></motion.div>
              <p className="font-head uppercase tracking-[0.35em] text-cyan-300 text-sm mb-2">Bonus Round</p>
              <h2 className="font-display font-black text-3xl text-white">Favou-Rite or Favou-Rong</h2>
              <p className="text-white/60 font-head mt-3 max-w-xs">One hard question from your favourite topic. <span className="text-[#00FF66] font-bold">+1000</span> if right, <span className="text-rose-400 font-bold">−1000</span> if wrong!</p>
              {me.favourite_category && <p className="mt-4 glass rounded-full px-4 py-1.5 border border-[#FFD700]/30 font-head text-[#FFD700] text-sm">Your topic: {me.favourite_category}</p>}
            </Center>
            {isHost ? <div className="p-4"><BigButton testid="control-begin-bonus" disabled={busy} onClick={() => hostAct("begin-round")} variant="green"><Play className="w-6 h-6" fill="currentColor" /> Start Bonus Round</BigButton></div>
              : <p className="text-center text-white/50 font-head pb-8">{remote ? "Get ready… your question's coming 📱" : "Get ready… watch the TV 📺"}</p>}
          </div>
        )}

        {status === "bonus" && state.current && (
          <div className="flex-1 flex flex-col p-4 gap-3">
            {state.can_answer ? (
              <>
                <div className="text-center">
                  <p className="font-display font-black text-lg text-[#FFD700] uppercase">{state.current.category} · Hard</p>
                  <p data-testid="mobile-bonus-status" className="font-head text-white/70 text-sm"><span className="text-[#00FF66] font-bold">+1000</span> right · <span className="text-rose-400 font-bold">−1000</span> wrong ⚡</p>
                </div>
                <div className="glass rounded-2xl p-4 border border-white/10 text-center"><p data-testid="mobile-bonus-question" className="font-display font-bold text-lg text-white">{state.current.question}</p></div>
                <div className="flex-1 grid grid-cols-1 gap-3">
                  {state.current.options.map((opt, i) => { const o = OPTIONS[i]; return (
                    <motion.button key={i} data-testid={`mobile-bonus-choice-${o.key}`} onClick={() => submit(i)} whileTap={{ scale: 0.95 }}
                      className={`${o.bg} ${o.active} ${o.glow} rounded-3xl flex items-center gap-4 px-6 border-4 ${o.border} flex-1`}>
                      <ShapeIcon index={i} className="w-9 h-9 text-white/90" /><span className="font-head font-bold text-xl text-white text-left">{opt}</span>
                    </motion.button>); })}
                </div>
              </>
            ) : <LockedIn choice={state.my_answer ?? picked} />}
            {isHost && <button data-testid="control-reveal-bonus" onClick={() => hostAct("reveal")} className="glass rounded-2xl py-3 font-head font-bold text-white/80 border border-white/15 flex items-center justify-center gap-2"><Eye className="w-5 h-5" /> Reveal now</button>}
          </div>
        )}

        {status === "bonus_reveal" && (
          <div className="flex-1 flex flex-col">
            <BonusRevealResult res={state.my_result} />
            {isHost && <div className="p-4"><BigButton testid="control-bonus-final" disabled={busy} onClick={() => hostAct("next")}><Trophy className="w-6 h-6" /> Final Results</BigButton></div>}
          </div>
        )}

        {status === "finished" && (
          <div className="flex-1 flex flex-col">
            <FinishedPlayer state={state} me={me} />
            {isHost ? <div className="p-4 space-y-3">
                <BigButton testid="control-play-again" disabled={busy} onClick={() => hostAct("restart")} variant="green"><RotateCcw className="w-5 h-5" /> Play Again</BigButton>
                <button data-testid="control-change-settings" disabled={busy} onClick={() => hostAct("reconfigure")} className="w-full glass rounded-2xl py-4 font-display font-black text-lg uppercase text-white border border-white/15 flex items-center justify-center gap-2"><Settings2 className="w-5 h-5" /> Change Settings</button>
                <button data-testid="control-new-players" disabled={busy} onClick={async () => { await hostAct("full-reset"); onLeave && onLeave(); }} className="w-full rounded-2xl py-3.5 font-head font-bold text-sm uppercase border border-rose-500/40 text-rose-300 flex items-center justify-center gap-2"><Users className="w-4 h-4" /> New Players (full reset)</button>
              </div>
              : <div className="p-4"><button data-testid="mobile-leave-button" onClick={onLeave} className="w-full glass border border-white/15 rounded-2xl py-3 font-head font-bold text-white/70">Leave</button></div>}
          </div>
        )}
        <JukeboxBar code={code} hostToken={hostToken} isHost={isHost} playerName={me?.name} mysteryMode={r?.mode === "hit_that" && (status === "question" || status === "reveal")} />
      </div>
    </div>
  );
}

function lastStep(state) {
  const r = state.round;
  if (!r) return false;
  return r.q_index + 1 >= r.q_total || state.payouts != null;
}

function RoundBanner({ r }) {
  const Icon = MODE_ICON[r.mode] || Zap;
  return (
    <Center>
      <p className="font-head uppercase tracking-[0.35em] text-cyan-300 text-sm mb-3">Round {r.index + 1} of {r.total}</p>
      <motion.div initial={{ scale: 0.6, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} transition={{ type: "spring" }} className="w-24 h-24 rounded-3xl bg-[#FF2A85]/20 glow-magenta flex items-center justify-center mb-5"><Icon className="w-12 h-12 text-[#FF2A85]" /></motion.div>
      <h2 className="font-display font-black text-3xl text-white">{r.mode_name}</h2>
      <p className="text-white/60 font-head mt-3 max-w-xs">{r.mode_desc}</p>
    </Center>
  );
}

function GetReadyNote() {
  return (
    <Center>
      <motion.div animate={{ scale: [1, 1.12, 1] }} transition={{ repeat: Infinity, duration: 1.1 }} className="w-20 h-20 rounded-full bg-[#1DB954]/20 flex items-center justify-center mx-auto mb-5">
        <Music className="w-10 h-10 text-[#1DB954]" />
      </motion.div>
      <p data-testid="mobile-get-ready" className="font-display font-black text-3xl text-white">Get ready…</p>
      <p className="font-head text-white/55 text-base mt-2">🎧 The snippet's cueing up on the TV</p>
    </Center>
  );
}

function SpectatorNote({ state, me, remote }) {
  let msg = "Watching…";
  if (me.lives === 0) msg = remote ? "You're out of lives 💀" : "You're out of lives 💀 — watch the TV";
  else if (state.round?.mode === "nominator" && state.active_player_name) msg = `${state.active_player_name} is answering…`;
  return <Center><Eye className="w-12 h-12 text-white/40 mx-auto mb-4" /><p className="font-head text-white/70 text-lg">{msg}</p>{remote && state.current && <div className="glass rounded-2xl px-4 py-3 border border-white/10 mt-4 mx-4"><p className="font-display font-bold text-base text-white">{state.current.question}</p></div>}</Center>;
}

function LockedIn({ choice, remote }) {
  const o = OPTIONS[choice] || OPTIONS[0];
  return (
    <motion.div initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className={`flex-1 rounded-3xl flex flex-col items-center justify-center gap-4 ${o.bg} ${o.glow} border-4 ${o.border}`}>
      <motion.div animate={{ scale: [1, 1.15, 1] }} transition={{ repeat: Infinity, duration: 1.4 }}><div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center"><Check className="w-14 h-14 text-white" strokeWidth={3} /></div></motion.div>
      <p className="font-display font-black text-3xl text-white uppercase">Locked In!</p>
      <div className="flex items-center gap-2 text-white/90"><ShapeIcon index={choice} className="w-7 h-7" /><span className="font-head font-bold text-lg">{remote ? "Answer locked 🔒" : "Watch the TV ⚡"}</span></div>
    </motion.div>
  );
}

// Remote-only: the question + options preview shown on the player's own screen.
function RemoteQuestion({ state }) {
  const cur = state.current;
  return (
    <div className="glass rounded-2xl px-4 py-3 border border-[#00F0FF]/30 text-center">
      <p className="font-head uppercase tracking-widest text-[#00F0FF] text-[10px] mb-1">{cur.category}</p>
      <p data-testid="remote-question-text" className="font-display font-bold text-lg text-white leading-snug">{cur.question}</p>
    </div>
  );
}

// Remote-only: correct answer + compact leaderboard after reveal.
function RemoteReveal({ state, me }) {
  const cur = state.current;
  const correctText = cur && cur.correct != null ? cur.options[cur.correct] : null;
  const ranked = state.players;
  return (
    <div className="px-4 pb-3 -mt-2 space-y-3">
      {correctText && <div data-testid="remote-correct-answer" className="glass rounded-2xl px-4 py-3 border border-[#00FF66]/40 text-center"><p className="font-head uppercase tracking-widest text-[#00FF66] text-[10px] mb-1">Correct answer</p><p className="font-display font-black text-xl text-white">{correctText}</p></div>}
      <div className="glass rounded-2xl p-2 border border-white/10">
        {ranked.slice(0, 5).map((p, i) => (
          <div key={p.id} className={`flex items-center gap-2.5 px-2 py-1.5 rounded-xl ${p.id === me.id ? "bg-[#FF2A85]/15" : ""}`}>
            <span className="font-display font-black text-white/50 w-5 text-center text-sm">{i + 1}</span>
            <Avatar player={p} size={30} />
            <span className="font-head font-semibold text-white text-sm flex-1 truncate">{p.name}{p.id === me.id && " (you)"}</span>
            <span className="font-display font-black text-[#00F0FF] text-sm">{p.score}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RevealResult({ state, me, remote }) {
  const res = state.my_result;
  const correct = res?.correct;
  const pts = res?.points || 0;
  if (correct) return (
    <Center><motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }}>
      <div className="w-24 h-24 rounded-full bg-[#00FF66] glow-green flex items-center justify-center mx-auto mb-5"><Check className="w-14 h-14 text-[#0D0714]" strokeWidth={3} /></div>
      <p className="font-display font-black text-3xl text-[#00FF66]">Correct!</p>
      {pts > 0 ? <p className="font-display font-black text-5xl text-white mt-2">+{pts}</p> : <p className="text-white/60 font-head mt-2">Life saved ❤️</p>}
    </motion.div></Center>
  );
  return (
    <Center><motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }}>
      <div className="w-24 h-24 rounded-full bg-rose-600 flex items-center justify-center mx-auto mb-5"><span className="text-5xl">😢</span></div>
      <p className="font-display font-black text-3xl text-rose-400">{res && res.choice != null ? "Not quite" : "No answer"}</p>
      {!remote && <p className="text-white/60 font-head mt-2">Check the TV for the answer</p>}
    </motion.div></Center>
  );
}

function BonusRevealResult({ res }) {
  const correct = res?.correct;
  const pts = res?.points || 0;
  const noAnswer = !res || res.choice == null;
  return (
    <Center><motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring" }}>
      <div className={`w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-5 ${correct ? "bg-[#00FF66] glow-green" : "bg-rose-600"}`}>
        {correct ? <Check className="w-14 h-14 text-[#0D0714]" strokeWidth={3} /> : <X className="w-14 h-14 text-white" strokeWidth={3} />}
      </div>
      <p className={`font-display font-black text-3xl ${correct ? "text-[#00FF66]" : "text-rose-400"}`}>{correct ? "Favou-RITE! 🎉" : noAnswer ? "No answer" : "Favou-RONG 😬"}</p>
      <p className={`font-display font-black text-5xl mt-2 ${pts >= 0 ? "text-white" : "text-rose-400"}`}>{pts >= 0 ? `+${pts}` : pts}</p>
      <p className="text-white/50 font-head mt-3">Check the TV for the final scores</p>
    </motion.div></Center>
  );
}

function FinishedPlayer({ state, me }) {
  const rank = state.players.findIndex((p) => p.id === me.id) + 1;
  const won = rank === 1;
  return (
    <Center>
      {won ? <Trophy className="w-20 h-20 text-[#FFD700] mx-auto mb-4" fill="#FFD700" /> : <div className="font-display font-black text-6xl text-[#00F0FF] mb-2">#{rank}</div>}
      <p className="font-display font-black text-4xl text-white">{won ? "You won! 🏆" : "Game Over"}</p>
      <p className="font-head text-white/60 mt-3">Final score</p>
      <p className="font-display font-black text-5xl text-[#00FF66] mt-1">{me.score}</p>
      <p className="font-head text-white/50 mt-4">{me.correct} correct · {me.incorrect} missed</p>
    </Center>
  );
}
